import type {
  ContractDetailDTO,
  ContractItemDTO,
  CreateFormSubmissionDTO,
  DocumentHubStatusDTO,
  FormDetailDTO,
  FormItemDTO,
  FormListQueryDTO,
  FormSubmissionResultDTO,
  PolicyItemDTO,
  RecentDocumentDTO,
  TemplateItemDTO,
  TemplateListQueryDTO,
} from "@/modules/document-portal/domain/contracts";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = () => `${config.apiUrl}/documents`;

export type FormCategoryDTO = { id: string; name: string };

/** Base URL for InfiniteCombobox (template department filter) */
export const documentsTemplateDepartmentsLookupUrl = () =>
  `${config.apiUrl}/documents/templates/departments/lookup`;

export const documentsApi = {
  forms: {
    listCategories(): Promise<{ categories: FormCategoryDTO[] }> {
      return fetcher.get(`${base()}/forms/categories`);
    },
    list(query: FormListQueryDTO): Promise<{
      data: FormItemDTO[];
      meta: { total: number; limit: number; offset: number };
    }> {
      const url = new URL(`${base()}/forms`);
      url.searchParams.set("limit", String(query.limit ?? 6));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.category && query.category !== "all")
        url.searchParams.set("category", query.category);
      if (query.search) url.searchParams.set("search", query.search);
      return fetcher.get(url.toString());
    },
    getById(id: string): Promise<FormDetailDTO> {
      return fetcher.get(`${base()}/forms/${id}`);
    },
    submit(
      id: string,
      payload: CreateFormSubmissionDTO,
    ): Promise<FormSubmissionResultDTO> {
      return fetcher.post(`${base()}/forms/${id}/submissions`, payload);
    },
  },
  templates: {
    list(query: TemplateListQueryDTO): Promise<{
      data: TemplateItemDTO[];
      meta: { total: number; limit: number; offset: number };
    }> {
      const url = new URL(`${base()}/templates`);
      url.searchParams.set("limit", String(query.limit ?? 6));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.departmentId?.trim())
        url.searchParams.set("departmentId", query.departmentId.trim());
      if (query.search) url.searchParams.set("search", query.search);
      return fetcher.get(url.toString());
    },
  },
  contracts: {
    list(): Promise<{
      data: ContractItemDTO[];
      meta: { total: number; limit: number; offset: number };
    }> {
      return fetcher.get(`${base()}/contracts`);
    },
    getById(id: string): Promise<ContractDetailDTO> {
      return fetcher.get(`${base()}/contracts/${id}`);
    },
  },
  policies: {
    list(): Promise<{
      data: PolicyItemDTO[];
      meta: { total: number; limit: number; offset: number };
    }> {
      return fetcher.get(`${base()}/policies`);
    },
    getById(id: string): Promise<PolicyItemDTO> {
      return fetcher.get(`${base()}/policies/${id}`);
    },
  },
  recent(limit?: number): Promise<RecentDocumentDTO[]> {
    const url = new URL(`${base()}/recent`);
    if (limit != null) url.searchParams.set("limit", String(limit));
    return fetcher.get(url.toString());
  },
  status(): Promise<DocumentHubStatusDTO> {
    return fetcher.get(`${base()}/status`);
  },
};
